import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import CustomDatePicker from "../../CustomDatePicker";
import { format } from "date-fns";

const validationSchema = Yup.object().shape({
  termsAndConditionSignatureDate: Yup.string().required(
    "*Signature Date is required"
  ),
  agree: Yup.boolean()
    .oneOf([true], "*You must accept Terms and Conditions")
    .required("*You must accept Terms and Conditions"),
});

const EditTermsAndCondition = forwardRef(
  ({ formData, setFormData, setLoadIndicators }, ref) => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [showImage, setShowImage] = useState(true);
    const userName = localStorage.getItem("userName");

    const [sign, setSign] = useState(null);
    const [url, setUrl] = useState(null);

    // Clear the signature canvas
    const handleClear = () => {
      if (sign) {
        sign.clear();
        setUrl(null);
      }
    };

    // Generate the signature data URL
    const handleGenerate = () => {
      if (sign && !sign.isEmpty()) {
        const signatureUrl = sign.getTrimmedCanvas().toDataURL("image/png");
        setUrl(signatureUrl);
        setShowImage(true);
        setIsEditing(false);
      } else {
        toast.error("Please provide a signature before saving.");
      }
    };

    // Enable edit mode for the signature
    const handleEdit = () => {
      setIsEditing(true);
      setShowImage(false);
    };

    // Cancel edit mode
    const handleCancel = () => {
      setIsEditing(false);
      setShowImage(true);
    };

    const formik = useFormik({
      initialValues: {
        file: null,
        termsAndConditionSignatureDate:
          formData.termsAndConditionSignatureDate || "",
        agree: formData.agree || false,
        studentDetailId: formData.id || "",
        stdTermsAndConditionId: null,
        updatedBy: userName,
      },
      validationSchema: validationSchema,
      onSubmit: async (data) => {
        setLoadIndicators(true);
        try {
          const formDatas = new FormData();

          // Append common fields
          formDatas.append(
            "termsAndConditionSignatureDate",
            data.termsAndConditionSignatureDate
          );
          formDatas.append("agree", data.agree);
          formDatas.append("updatedBy", data.updatedBy);

          // Handle signature
          if (url) {
            const randomNumber = Math.floor(Math.random() * 100000);
            const apiResponse = await fetch(url);
            const blob = await apiResponse.blob();
            formDatas.append("file", blob, `${randomNumber}Signature.png`);
          } else if (!data.stdTermsAndConditionId) {
            // If creating a new record and no signature, throw an error
            throw new Error("Signature is required for submission.");
          }

          if (data.stdTermsAndConditionId) {
            // Update existing record
            formDatas.append(
              "studentTermsAndConditionId",
              data.stdTermsAndConditionId
            );

            const response = await api.put(
              `/updateStudentTermsAndConditions/${data.stdTermsAndConditionId}`,
              formDatas,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            if (response.status === 200) {
              toast.success(response.data.message);
              navigate("/student");
            } else {
              toast.error(response.data.message);
            }
          } else {
            // Create new record
            formDatas.append("studentDetailId", formData.id);

            const response = await api.post(
              `/createStudentTermsAndConditions`,
              formDatas,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            if (response.status === 201) {
              toast.success(response.data.message);
              setFormData((prev) => ({ ...prev, ...data }));
              navigate("/student");
            } else {
              toast.error(response.data.message);
            }
          }
        } catch (error) {
          if (
            error?.response?.status === 404 ||
            error?.response?.status === 409
          ) {
            toast.warning(error?.response?.data?.message || error.message);
          } else {
            toast.error(error?.response?.data?.message || error.message);
          }
        } finally {
          setLoadIndicators(false);
        }
      },
    });

    // Scroll to the first error field after form submission
    useEffect(() => {
      if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
        const errorField = Object.keys(formik.errors)[0];
        const errorElement = document.querySelector(`[name="${errorField}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }
    }, [formik.submitCount, formik.errors]);

    // Fetch initial data
    useEffect(() => {
      const getData = async () => {
        try {
          const response = await api.get(`/getAllStudentById/${formData.id}`);
          const termsData = response.data.studentTermsAndConditions?.[0];

          if (termsData) {
            formik.setValues({
              ...termsData,
              termsAndConditionSignatureDate: termsData.termsAndConditionSignatureDate
                ? termsData.termsAndConditionSignatureDate.substring(0, 10)
                : "",
              stdTermsAndConditionId: termsData.id,
              agree: termsData.agree || false,
              updatedBy: userName,
            });
            setUrl(termsData.parentSignature || null); // Set initial signature URL
            setData(response.data);
          } else {
            formik.setValues({
              file: null,
              termsAndConditionSignatureDate: "",
              agree: false,
              studentDetailId: formData.id,
              stdTermsAndConditionId: null,
              updatedBy: userName,
            });
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          toast.error("Failed to fetch terms and conditions data.");
        }
      };
      getData();
    }, [formData.id, userName]);

    // Expose the submit function to the parent component
    useImperativeHandle(ref, () => ({
      termsAndCondition: formik.handleSubmit,
    }));

    return (
      <div className="container-fluid">
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !formik.isSubmitting) {
              e.preventDefault();
            }
          }}
        >
          <div className="border-0 mb-5">
            <div className="mb-5">
              <div className="border-0 my-2 px-2">
                <p className="headColor">Terms and Conditions</p>
                <div className="container-fluid py-3">
                  <div className="row">
                    <div className="col-md-6 col-12">
                      <div className="text-start mt-3">
                        <label className="mb-1 fw-medium">
                          <small>Parent Signature</small>
                        </label>
                        <br />
                        <div className="border border-secondary rounded-2 parentSignature">
                          {showImage && url ? (
                            <img
                              src={url}
                              className="img-fluid rounded ms-3 mt-2"
                              style={{ maxWidth: "40%" }}
                              alt="Parent Signature"
                            />
                          ) : (
                            <div>
                              <SignatureCanvas
                                canvasProps={{
                                  width: 423,
                                  height: 150,
                                  className: "sigCanvas",
                                }}
                                ref={(ref) => setSign(ref)}
                              />
                              <br />
                              <button
                                type="button"
                                style={{ height: "30px", width: "60px" }}
                                onClick={handleClear}
                                className="btn btn-sm bg-light mx-1"
                              >
                                Clear
                              </button>
                              <button
                                type="button"
                                style={{ height: "30px", width: "60px" }}
                                onClick={handleGenerate}
                                className="btn btn-sm bg-light mx-1"
                              >
                                Save
                              </button>
                              {isEditing && (
                                <button
                                  type="button"
                                  style={{ height: "30px", width: "70px" }}
                                  onClick={handleCancel}
                                  className="btn btn-sm bg-light ms-1"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <br />
                        {showImage && url && (
                          <button
                            type="button"
                            style={{ height: "30px", width: "60px" }}
                            onClick={handleEdit}
                            className="btn btn-sm bg-light"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6 col-12">
                      <div className="text-start mt-2">
                        <label className="mb-1 fw-medium">
                          <small>
                            Signature Date<span className="text-danger">*</span>
                          </small>
                        </label>
                        <br />
                        <CustomDatePicker
                          name="termsAndConditionSignatureDate"
                          value={
                            formik.values.termsAndConditionSignatureDate
                              ? new Date(
                                  formik.values.termsAndConditionSignatureDate
                                )
                              : null
                          }
                          onChange={(date) =>
                            formik.setFieldValue(
                              "termsAndConditionSignatureDate",
                              date ? format(date, "yyyy-MM-dd") : ""
                            )
                          }
                          onBlur={formik.handleBlur}
                          error={formik.errors.termsAndConditionSignatureDate}
                          touched={
                            formik.touched.termsAndConditionSignatureDate
                          }
                          dateFormat="yyyy-MM-dd"
                        />
                        {formik.touched.termsAndConditionSignatureDate &&
                          formik.errors.termsAndConditionSignatureDate && (
                            <div className="error text-danger">
                              {formik.errors.termsAndConditionSignatureDate}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      name="agree"
                      checked={formik.values.agree}
                      onChange={(e) => {
                        formik.setFieldValue("agree", e.target.checked);
                      }}
                      onBlur={formik.handleBlur}
                    />
                    <small>
                      By submitting this form, I confirm that I have read and
                      agree to Arty Learning's
                      <Link
                        to="/termsAndCondition"
                        target="_blank"
                        style={{ color: "#ff7500" }}
                        className="ms-1"
                      >
                        Terms & Conditions.
                      </Link>
                    </small>
                    {formik.touched.agree && formik.errors.agree && (
                      <div className="text-danger">
                        <small>{formik.errors.agree}</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }
);

export default EditTermsAndCondition;